export const STORAGE_SCHEMA_VERSION = 2;

export type EquityBps = number;
export type ISODateString = string;

export type AdverseEventReason =
  | 'none'
  | 'ordinary_termination'
  | 'service_stopped'
  | 'major_breach'
  | 'confidentiality_breach'
  | 'undisclosed_conflict'
  | 'non_compete_breach'
  | 'competing_project';

export type AchievementRoute = 'industrial_cooperation' | 'key_hire_six_months';

export type MilestoneStatus =
  | 'blocked'
  | 'pending'
  | 'future'
  | 'earned'
  | 'vesting'
  | 'exercisable'
  | 'exercised'
  | 'lapsed'
  | 'repurchase_80_risk'
  | 'repurchase_nominal_risk';

export interface Milestone {
  id: string;
  title: string;
  shortTitle: string;
  description: string;
  equityBps: EquityBps;
  achieved: boolean;
  achievedAt: ISODateString | null;
  sourceClause: string;
  sourceSummary: string;
  evidenceExamples: string[];
  legalNote: string;
  achievementRoute?: AchievementRoute[];
  selectedAchievementRoute?: AchievementRoute;
  condition?: string;
  displayOrder: number;
}

export interface Scenario {
  schemaVersion: number;
  asOfDate: ISODateString;
  conflictCleared: boolean;
  conflictClearedAt: ISODateString | null;
  serviceActive: boolean;
  agreementTerminated: boolean;
  terminationDate: ISODateString | null;
  adverseEventReason: AdverseEventReason;
  milestones: Milestone[];
  exercisedBpsByMilestone: Record<string, EquityBps>;
}

export interface DemoContract {
  schemaVersion: number;
  name: string;
  subtitle: string;
  demoEntityName: string;
  disclaimer: string;
  sourceNotice: string;
  assumptions: string[];
  scenario: Scenario;
}

export interface CalculationTrace {
  milestoneId: string;
  heading: string;
  steps: string[];
}

export interface MilestoneCalculation {
  milestone: Milestone;
  status: MilestoneStatus;
  potentialBps: EquityBps;
  milestoneEarnedBps: EquityBps;
  timeVestedBps: EquityBps;
  exercisableBps: EquityBps;
  exercisedBps: EquityBps;
  lapsedBps: EquityBps;
  blockedBps: EquityBps;
  repurchaseAt80RiskBps: EquityBps;
  repurchaseAtNominalRiskBps: EquityBps;
  vestingPercent: number;
  monthsElapsed: number;
  trace: CalculationTrace;
}

export interface EquitySummary {
  potentialBps: EquityBps;
  milestoneEarnedBps: EquityBps;
  timeVestedBps: EquityBps;
  exercisableBps: EquityBps;
  exercisedBps: EquityBps;
  lapsedBps: EquityBps;
  blockedBps: EquityBps;
  repurchaseAt80RiskBps: EquityBps;
  repurchaseAtNominalRiskBps: EquityBps;
}

export interface ScenarioCalculation {
  scenario: Scenario;
  summary: EquitySummary;
  milestones: MilestoneCalculation[];
  assumptions: string[];
}
